import React, { useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { H2 } from '../forms';
import { Body2, Button, Checkbox, LinkButton, TextLinkExternal } from '../../components/common';
import { useMessageGetter } from 'react-message-context';
import { InputField } from '../../components/common/forms';
import { Form, FormContainer } from '../layoutComponents';
import { YSaatioRoutes } from '../../constants/routes';
import FormError from '../../components/common/forms/FormError';
import { AcceptTermsFormState, acceptTermsSchema } from '../auth/schemas';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormRow } from '../forms/layoutComponents';
import { DropdownOptionValue } from '../../components/common/Dropdown';
import { initiateStrongAuthentication } from '../../apis_kenno';
import { FlexColumn, FlexRow } from '../../components/basic/Flex';
import styled from 'styled-components/macro';

const InstructionsList = styled.ol`
  margin: 0;
`;

const YSaatioStrongAuthStep = () => {
  const ySaatioTranslationPrefix = 'ysaatio.signup';
  const translationPrefix = 'signup';
  const t = useMessageGetter(translationPrefix);
  const tYSaatio = useMessageGetter(ySaatioTranslationPrefix);

  const [stepFailed, setStepFailed] = useState(false);
  const formMethods = useForm<AcceptTermsFormState>({ resolver: zodResolver(acceptTermsSchema) });

  const onSubmit: SubmitHandler<AcceptTermsFormState> = async () => {
    try {
      const { url, state } = await initiateStrongAuthentication({ flowType: 'ysaatio-registration' });
      // Session storage required for this since user is moved to external OIDC provider
      window.sessionStorage.setItem('oauthState', state);
      window.location.href = url;
    } catch (error) {
      // Terms were not accepted
      setStepFailed(true);
    }
  };

  return (
    <FormContainer>
      <FormProvider {...formMethods}>
        <H2>{tYSaatio('title')}</H2>
        <FlexColumn>
          <Body2>{tYSaatio('instructions1')}</Body2>
          <Body2>{tYSaatio('instructionsListTitle')}</Body2>
          <InstructionsList>
            <li>{tYSaatio('instructionsList1')}</li>
            <li>{tYSaatio('instructionsList2')}</li>
            <li>{tYSaatio('instructionsList3')}</li>
          </InstructionsList>
          <Body2>{tYSaatio('instructions2')}</Body2>
          <Body2>{tYSaatio('instructions3')}</Body2>
        </FlexColumn>
        <Form onSubmit={formMethods.handleSubmit(onSubmit)}>
          <InputField<AcceptTermsFormState, DropdownOptionValue>
            fieldPath="termsAccepted"
            translationPrefix={translationPrefix}
            hideLabel
            dark
            inputElementRenderer={({ value, id, onChange }) => (
              <FormRow style={{ gap: '12px' }}>
                <Checkbox value={!!value} id={id} onChange={onChange} />
                <div>
                  {`${t('accept')} `}
                  <TextLinkExternal
                    href="https://m2kodit.fi/kayttoehdot-ja-henkilotietosuoja/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('termsOfUse')}
                  </TextLinkExternal>
                </div>
              </FormRow>
            )}
          />
          {stepFailed && <FormError message={t('generalError')} />}
          <FlexRow gap="16px" wrap="wrap">
            <Button type="submit" id="submit" primary>
              {tYSaatio('continue')}
            </Button>
            <LinkButton to={YSaatioRoutes.SIGN_IN}>{tYSaatio('toSignIn')}</LinkButton>
          </FlexRow>
        </Form>
      </FormProvider>
    </FormContainer>
  );
};

export default YSaatioStrongAuthStep;
